/**
 * Cookie consent store
 *
 * Analytics cookies may only be set after the user opts in, so the decision
 * is persisted here and read by src/utils/analytics.ts before anything is
 * initialized or reported. A missing decision (null) means "not asked yet"
 * and must be treated as no consent.
 */

import { logger } from './logger';

export type ConsentValue = 'granted' | 'denied';

const STORAGE_KEY = 'zlomiarz_cookie_consent';

// undefined = not read from storage yet, null = no decision stored
let cachedConsent: ConsentValue | null | undefined;

const listeners = new Set<() => void>();

const isConsentValue = (value: unknown): value is ConsentValue =>
  value === 'granted' || value === 'denied';

const readStoredConsent = (): ConsentValue | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isConsentValue(stored) ? stored : null;
  } catch (error) {
    // Private browsing or blocked storage — fall back to in-memory only
    logger.debug('Cookie consent could not be read', undefined, {
      component: 'CookieConsent',
      operation: 'Read',
    });
    return null;
  }
};

const notify = (): void => {
  listeners.forEach(listener => listener());
};

/**
 * The user's current decision, or null when they have not been asked yet.
 */
export const getConsent = (): ConsentValue | null => {
  if (cachedConsent === undefined) {
    cachedConsent = readStoredConsent();
  }
  return cachedConsent;
};

export const hasAnalyticsConsent = (): boolean => getConsent() === 'granted';

/**
 * Record a decision and persist it across sessions.
 */
export const setConsent = (value: ConsentValue): void => {
  cachedConsent = value;

  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (error) {
    logger.debug('Cookie consent could not be persisted', undefined, {
      component: 'CookieConsent',
      operation: 'Write',
    });
  }

  notify();
};

/**
 * Clear the stored decision so the banner is shown again. Used by the
 * "change cookie settings" controls — withdrawing consent has to be as easy
 * as giving it.
 */
export const resetConsent = (): void => {
  cachedConsent = null;

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.debug('Cookie consent could not be cleared', undefined, {
      component: 'CookieConsent',
      operation: 'Clear',
    });
  }

  notify();
};

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 */
export const subscribeConsent = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
